import { AspectRatio, Avatar, Image } from '@mantine/core'
import { IconChevronDown, IconUser } from '@tabler/icons-react'
import { Typography } from '../../common/typography'
import { CompetitionTeam } from '../../performance/api'
import ComparePickerComponent from '../Compare/ComparePickerComponent'
import classes from './SubjectSelector.module.css'
import { useCompetitionId } from '../../common/hooks'
import { usePlayerRanks, useTeamRanks } from '../../ranks/api'
import { CompareSubject } from '../../compare/Compare'

interface SubjectSelectorProps {
  opened: boolean
  open: () => void
  close: () => void
  teamWithPlayers: CompetitionTeam[]
  subject: CompareSubject
  setSubject: React.Dispatch<React.SetStateAction<CompareSubject>>
}

export default function SubjectSelector({
  opened,
  open,
  close,
  teamWithPlayers,
  subject,
  setSubject,
}: SubjectSelectorProps) {
  const competition_id = useCompetitionId()

  // Fetch rank data for display
  const { data: playerRanksResponse } = usePlayerRanks({
    competition_id: Number(competition_id),
  })
  const { data: teamRanksResponse } = useTeamRanks({
    competition_id: Number(competition_id),
  })

  const playerRanks = playerRanksResponse.items
  const teamRanks = teamRanksResponse.items

  // Find rank and rating for selected subject
  let rank: number | null = null
  let rating: number | null = null

  if (subject?.type === 'user') {
    const playerRank = playerRanks?.find((p) => p.player_id === subject.id)
    rank = playerRank?.rank ?? null
    rating = playerRank?.rating ?? null
  } else if (subject?.type === 'team') {
    const teamRank = teamRanks?.find((t) => t.team_id === subject.id)
    rank = teamRank?.rank ?? null
    rating = teamRank?.rating ?? null
  }

  return (
    <>
      <div className={classes.container} onClick={open}>
        <div className={classes.content}>
          <div className={classes.avatarContainer}>
            {subject ? (
              <AspectRatio ratio={1 / 1} className={classes.aspectRatio}>
                <Image
                  src={subject.photo}
                  alt={subject.name}
                  className={classes.subjectImage}
                  fit="scale-down"
                />
              </AspectRatio>
            ) : (
              <Avatar className={classes.avatar}>
                <IconUser size={20} />
              </Avatar>
            )}
          </div>

          <div className={classes.info}>
            <Typography
              fw={500}
              lh="130"
              fc={{ light: 'gray-7', dark: 'gray-3' }}
            >
              {subject ? subject.name : 'Select Team or Player'}
            </Typography>
          </div>

          {subject && rank !== null && rating !== null && (
            <div className={classes.stats}>
              <div className={classes.rankBadge}>
                <Typography
                  fs="xs"
                  fw={700}
                  fc={{ light: 'primary-5', dark: 'primary-4' }}
                >
                  {rank}
                  {getRankSuffix(rank)}
                </Typography>
              </div>
              <Typography
                fs="md"
                fw={700}
                fc={{ light: 'gray-9', dark: 'gray-3' }}
                className={classes.rating}
              >
                {rating.toFixed(2)}
              </Typography>
            </div>
          )}

          <IconChevronDown className={classes.chevron} />
        </div>
      </div>

      <ComparePickerComponent
        opened={opened}
        close={close}
        teamWithPlayers={teamWithPlayers}
        subject={subject}
        setSubject={setSubject}
      />
    </>
  )
}

function getRankSuffix(rank: number): string {
  const j = rank % 10
  const k = rank % 100
  if (j === 1 && k !== 11) {
    return 'st'
  }
  if (j === 2 && k !== 12) {
    return 'nd'
  }
  if (j === 3 && k !== 13) {
    return 'rd'
  }
  return 'th'
}
